import { useSite } from "@inrange/building-manager-api-client";
import {
  AggregateEnergyFlow,
  AllowStringForNumbers,
  EditMode,
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
  SiteElectricalSystem,
  SiteElectricalSystemPlaceholder,
} from "@inrange/building-manager-api-client/models-site";
import { BorderedCheckbox, Tooltip } from "@inrange/theme-components";
import { useContextTS } from "@inrange/utils";
import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Form, Row, Stack, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { SiteCalculationsContext } from "../../../../../shared-components/src/SiteCalculationsContext";
import { demandFileUrl } from "../../demanddata/demandDataLinks";
import FormInputPercentage from "../../FormInputPercentage";
import CollapsibleCard from "../CollapsibleCard";
import {
  DEMAND_FORECAST_LABELS,
  USAGE_DAYS_LABELS,
  USAGE_PROFILE_LABELS,
} from "./enums";
import GridConnection from "./GridConnection";
import {
  formatNumber,
  formatNumber0dp,
  formatNumber1dp,
  formatNumber2dp,
  parseAsNumber,
  roundToDp,
} from "./utils";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const daysInMonth = (year: number, month: number): number => {
  // trick is to create a date for the next month and use day 0, which automatically rolls back to
  // the last day of the previous month. That gives you the number of days in the target month.
  // month is 1-based (1 = Jan, 12 = Dec)
  return new Date(year, month, 0).getDate();
};

const Demand = ({
  site,
  esys,
  matchSuffix,
  setSite,
  editMode,
  handleAddSiteMatch,
}: {
  site: SiteAllowStringValues;
  esys:
    | AllowStringForNumbers<SiteElectricalSystem>
    | SiteElectricalSystemPlaceholder;
  matchSuffix?: string;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  editMode: EditMode;
  handleAddSiteMatch?: () => void;
}) => {
  const { errors, siteCalculations, setClientsideValidations } = useContextTS(
    SiteCalculationsContext
  );
  const haveDemandCurve =
    site.demand[esys.demandIdx] &&
    !!site.demand[esys.demandIdx].halfHourlyDemand?.source &&
    site.demand[esys.demandIdx].halfHourlyDemand?.source !== "inferred";

  const energyFlowAnnual =
    editMode === "SUMMARY"
      ? siteCalculations.energyFlowAnnual
      : siteCalculations?.esys?.[esys.idx]?.energyFlowAnnual;
  const energyFlowMonthly =
    editMode === "SUMMARY"
      ? siteCalculations.energyFlowMonthly
      : (siteCalculations?.esys?.[esys.idx]?.energyFlowMonthly as unknown as {
          [month: number]: AggregateEnergyFlow;
        });

  const computedDemandCoefficient =
    (site.totalBuildingArea as number) > 0
      ? (energyFlowAnnual?.demand || 0) / (site.totalBuildingArea as number)
      : 0;

  const { fetchSiteValues } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });
  const [displayMonthlyBreakdown, setDisplayMonthlyBreakdown] = useState(false);
  const [displayDemandMetadata, setDisplayDemandMetadata] = useState(false);

  const demandSource = useMemo(() => {
    if (editMode === "SUMMARY") return undefined;
    const hh = site.demand[esys.demandIdx].halfHourlyDemand;
    if (hh?.source === "hh_data") {
      const meta = hh.metadata;
      const start = meta?.fullyear_data_start;
      const end = meta?.fullyear_data_end;
      let label: string;
      if (start && end) {
        const fmt = (d: string) => {
          const [y, m] = d.split("-");
          return `${months[parseInt(m, 10) - 1]} ${y}`;
        };
        label = `Actual ${fmt(start)} to ${fmt(end)}`;
      } else {
        label = "Actual (input range unknown)";
      }
      return <Badge bg="success">{label}</Badge>;
    }
    if (hh?.source === "generated") {
      return <Badge bg="warning">Generated</Badge>;
    }
    return <Badge bg="warning">Inferred</Badge>;
  }, [editMode, site.demand, esys.demandIdx]);

  const demandQualityWarning = useMemo(() => {
    if (editMode === "SUMMARY") return null;
    const hh = site.demand[esys.demandIdx].halfHourlyDemand;
    if (hh?.source !== "hh_data") return null;
    const meta = hh.metadata;
    if (
      !meta?.latest_full_year_num_rows_customer_data ||
      !meta?.latest_full_year_num_rows
    )
      return null;

    const pct = Math.round(
      (parseAsNumber(meta.latest_full_year_num_rows_customer_data) /
        parseAsNumber(meta.latest_full_year_num_rows)) *
        100
    );
    const belowThreshold = pct < 85;
    const gapDays =
      meta.largest_gap_days !== undefined
        ? Math.round(meta.largest_gap_days)
        : undefined;
    const largeGap = gapDays !== undefined && gapDays > 14;
    const isWarning = belowThreshold || largeGap;

    let message = `Demand data: Normalised year ${pct}% directly from customer data`;
    if (belowThreshold) {
      message += ` (<85% threshold)`;
    }
    if (gapDays !== undefined) {
      message += `, largest gap ${gapDays} days`;
      if (largeGap) {
        message += ` (>14 day threshold)`;
      }
      message += ".";
    } else if (isWarning) {
      message += ". (Reprocess to check maximum gap length)";
    } else {
      message += ".";
    }

    const guid = site.demand[esys.demandIdx]?.hhDemandGuid;
    const detailsLink = guid ? demandFileUrl(guid, true) : null;

    return (
      <div
        className="mt-1 mb-1 p-2"
        style={{
          backgroundColor: isWarning ? "#fff3cd" : "#d1e7dd",
          border: `1px solid ${isWarning ? "#ffc107" : "#198754"}`,
          borderRadius: "4px",
          fontSize: "13px",
        }}
      >
        {isWarning ? "\u26A0\uFE0F" : "\u2705"} {message}
        {detailsLink && (
          <>
            {" "}
            <Link to={detailsLink}>View details</Link>
          </>
        )}
      </div>
    );
  }, [editMode, site.demand, esys.demandIdx]);

  const renderDemandMetadata = () => {
    const metadata = site?.demand[esys.demandIdx].halfHourlyDemand?.metadata;
    const haveDemandMetadata =
      haveDemandCurve &&
      metadata?.latest_full_year_num_rows_customer_data !== undefined &&
      metadata?.latest_full_year_num_rows != undefined;
    if (!haveDemandMetadata) return null;

    const haveInputSiteIds = !!metadata?.site_mapping?.["input_site_ids_list"];
    const haveTargetDemand =
      !!metadata?.site_mapping?.["overrides"]?.["total_demand"];

    const percentageRealData = (
      (parseAsNumber(metadata.latest_full_year_num_rows_customer_data) /
        parseAsNumber(metadata.latest_full_year_num_rows)) *
      100
    ).toFixed(2);
    return (
      <>
        <p>
          Metadata:{" "}
          <ul>
            <li>{percentageRealData}% of data directly from input HH data</li>
            <li>Input file: {metadata?.input_file ?? "Unknown"}</li>
            {metadata?.input_data_start && metadata?.input_data_end && (
              <li>
                Available input data period: {metadata.input_data_start} to{" "}
                {metadata.input_data_end}
              </li>
            )}
            <li>
              Selected full year:{" "}
              {metadata?.fullyear_data_start && metadata?.fullyear_data_end ? (
                <>
                  {metadata.fullyear_data_start} to {metadata.fullyear_data_end}
                </>
              ) : (
                <em>Reprocess demand data to make this metadata available</em>
              )}
            </li>
            {haveTargetDemand && (
              <li>Input data re-scaled to fixed target total demand</li>
            )}
            {haveInputSiteIds && (
              <li>
                Script input sites:
                <ul>
                  {metadata?.site_mapping?.["input_site_ids_list"].map(
                    (siteId: string) => (
                      <li key={siteId}>
                        <Link to={`/site/${siteId}/edit`}>{siteId}</Link>
                      </li>
                    )
                  )}
                </ul>
              </li>
            )}
            <li>
              Date file processed: {metadata?.date_of_analysis ?? "Unknown"}
            </li>
            {!!metadata?.site_mapping?.overrides?.existing_panels && (
              <li>
                Subtracted simulated generation from existing panels:{" "}
                {metadata?.site_mapping?.overrides?.existing_panels}
              </li>
            )}
            {site.demand[esys.demandIdx]?.hhDemandGuid && (
              <li>
                HH demand file GUID:{" "}
                <Link
                  to={demandFileUrl(site.demand[esys.demandIdx].hhDemandGuid!)}
                >
                  {site.demand[esys.demandIdx].hhDemandGuid}
                </Link>
              </li>
            )}
          </ul>
        </p>
      </>
    );
  };

  const getDaylightPercentage = (
    energyFlow: AggregateEnergyFlow | undefined,
    dp: number = 2
  ) => {
    if (
      !energyFlow ||
      energyFlow.daylightBehindMeter === undefined ||
      energyFlow.daylightDemand === undefined
    ) {
      return "N/A";
    }
    if (energyFlow.daylightDemand === 0) return "0";
    const result =
      (energyFlow.daylightBehindMeter / energyFlow.daylightDemand) * 100;
    return formatNumber(result, dp, dp, true);
  };

  const getDaylightKwh = (
    energyFlow: AggregateEnergyFlow | undefined,
    dp: number = 2
  ) => {
    if (!energyFlow || energyFlow.daylightBehindMeter === undefined) {
      return "N/A";
    }
    return formatNumber(energyFlow.daylightBehindMeter, dp, dp, true);
  };

  const maxBehindMeter = energyFlowMonthly
    ? Object.values(energyFlowMonthly).reduce((sum, month) => {
        return sum + Math.min(month.demand, month.generation);
      }, 0)
    : 0;

  const totalAreasPct = roundToDp(
    Object.values(site.demand).reduce((sum, demandSection) => {
      return sum + parseAsNumber(demandSection?.areaPct ?? 0);
    }, 0),
    1
  );
  useEffect(() => {
    setClientsideValidations((prev) => ({
      ...prev,
      [`demand.${esys.demandIdx}.areaPct`]:
        editMode === "ONE_OF_MULTIPLE" &&
        !haveDemandCurve &&
        totalAreasPct !== 1
          ? `The sum of all area portions must equal 100%. Currently at ${formatNumber1dp(totalAreasPct * 100)}%.`
          : undefined,
    }));
  }, [
    esys.demandIdx,
    editMode,
    haveDemandCurve,
    setClientsideValidations,
    totalAreasPct,
  ]);

  const summaryDemandKwh = `${formatNumber(energyFlowAnnual?.demand || 0, 2, 2)} kWh`;
  const summaryOnsiteShare = energyFlowAnnual?.tenantEnergyShare
    ? ` - ${formatNumber2dp(energyFlowAnnual.tenantEnergyShare * 100)}% on-site`
    : "";
  const existingPvKwp =
    editMode === "SUMMARY"
      ? Object.values(site.demand)
          .filter((demandSection) => !!demandSection)
          .reduce((sum, demandSection) => {
            return sum + parseAsNumber(demandSection.existingPvKwp);
          }, 0)
      : site.demand[esys.demandIdx]?.existingPvKwp;
  const summaryExistingSolar = existingPvKwp
    ? ` - ${formatNumber0dp(existingPvKwp)} kWp existing`
    : "";
  const hasExportLimit =
    site.esys[esys.idx]?.exportLimit !== "" &&
    site.esys[esys.idx]?.exportLimit !== null &&
    site.esys[esys.idx]?.exportLimit !== undefined;
  const summaryExportLimit = hasExportLimit
    ? ` - Export: ${site.esys[esys.idx]?.exportLimit} kVA`
    : "";

  return (
    <CollapsibleCard
      title={`Demand${matchSuffix || ""}`}
      summary={`${summaryDemandKwh}${summaryOnsiteShare}${summaryExistingSolar}${summaryExportLimit}`}
      className="mt-2"
      menu={
        handleAddSiteMatch ? (
          <Button
            onClick={handleAddSiteMatch}
            variant="success"
            style={{ padding: "2px 10px" }}
            data-testid="addSiteMatchButton"
          >
            +
          </Button>
        ) : undefined
      }
      initialOpen={editMode === "SINGLE"}
    >
      <Row className="d-flex flex-wrap align-items-end">
        {editMode !== "SUMMARY" && (
          <>
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`buildingProfile-${esys.demandIdx}`}
              >
                <Form.Label>Building profile</Form.Label>
                <Form.Select
                  value={site.demand[esys.demandIdx].buildingProfile}
                  onChange={(e) => {
                    const newProfile = e.target.value;
                    const profileData =
                      fetchSiteValues.data!.options.buildingProfiles[
                        newProfile
                      ]!;
                    setSite({
                      demand: {
                        [esys.demandIdx]: {
                          buildingProfile: newProfile,
                          demandCoefficientKWhPerM2:
                            profileData.demandCoefficient,
                          profileShape: profileData.profileShape,
                          profileDays: profileData.profileDays,
                        },
                      },
                    });
                  }}
                  data-testid={"buildingProfile"}
                >
                  {Object.entries(
                    fetchSiteValues.data!.options.buildingProfiles
                  ).map(([profileValue, profileData]) => (
                    <option key={profileValue} value={profileValue}>
                      {profileData.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </>
        )}
        {editMode === "ONE_OF_MULTIPLE" && (
          <>
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`areaPortion-${esys.demandIdx}`}
              >
                <Form.Label>Area portion (%)</Form.Label>
                <FormInputPercentage
                  value={site.demand[esys.demandIdx]?.areaPct ?? "0"}
                  onChange={(e) =>
                    setSite({
                      demand: {
                        [esys.demandIdx]: {
                          areaPct: e.target.value,
                        },
                      },
                    })
                  }
                  errorKey={`demand.${esys.demandIdx}.areaPct`}
                  testId={`areaPortion-${esys.demandIdx}`}
                />
              </Form.Group>
            </Col>
          </>
        )}
        {editMode !== "SUMMARY" && (
          <>
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`existingPvKwp-${esys.demandIdx}`}
              >
                <Form.Label>Existing solar (kWp)</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    site.demand[esys.demandIdx].existingPvKwp === undefined ||
                    site.demand[esys.demandIdx].existingPvKwp === null
                      ? ""
                      : formatNumber(
                          site.demand[esys.demandIdx].existingPvKwp,
                          3,
                          3,
                          false
                        )
                  }
                  onChange={(e) => {
                    setSite({
                      demand: {
                        [esys.demandIdx]: {
                          existingPvKwp: formatNumber(
                            e.target.value,
                            3,
                            3,
                            false
                          ),
                        },
                      },
                    });
                  }}
                  isInvalid={!!errors[`demand.${esys.demandIdx}.existingPvKwp`]}
                />
                <Form.Control.Feedback type="invalid">
                  {errors[`demand.${esys.demandIdx}.existingPvKwp`]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </>
        )}
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`demandCoefficient-${esys.demandIdx}`}
          >
            <Form.Label>Coefficient (kWh/m²)</Form.Label>
            <Form.Control
              type="text"
              value={formatNumber(
                editMode === "SUMMARY" || haveDemandCurve
                  ? computedDemandCoefficient
                  : site.demand[esys.demandIdx].demandCoefficientKWhPerM2,
                2,
                8,
                false
              )}
              onChange={(e) => {
                setSite({
                  demand: {
                    [esys.demandIdx]: {
                      demandCoefficientKWhPerM2: formatNumber(
                        e.target.value,
                        2,
                        8,
                        false
                      ),
                    },
                  },
                });
              }}
              isInvalid={
                !!errors[`demand.${esys.demandIdx}.demandCoefficientKWhPerM2`]
              }
              disabled={
                editMode === "SUMMARY" ||
                haveDemandCurve ||
                site.demand[esys.demandIdx].buildingProfile === "noDemand"
              }
              data-testid={"demandCoefficient"}
            />
            <Form.Control.Feedback type="invalid">
              {errors[`demand.${esys.demandIdx}.demandCoefficientKWhPerM2`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`totalBuildingArea-${esys.demandIdx}`}
          >
            <Form.Label>
              {editMode === "ONE_OF_MULTIPLE" ? "Portion" : "Total"} building
              area (m²)
            </Form.Label>
            <Form.Control
              disabled
              value={formatNumber(
                parseAsNumber(site.totalBuildingArea) *
                  (editMode === "ONE_OF_MULTIPLE"
                    ? parseAsNumber(site.demand[esys.demandIdx]?.areaPct ?? 1)
                    : 1),
                2,
                2
              )}
              data-testid={"totalBuildingArea"}
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`annualDemand-${esys.demandIdx}`}
          >
            <Form.Label>Annual demand (kWh)</Form.Label>
            <Form.Control
              type="text"
              value={formatNumber(energyFlowAnnual?.demand || 0, 2, 2)}
              disabled
              data-testid={"annualDemand"}
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`daylightDemand-${esys.demandIdx}`}
          >
            <Form.Label>Daylight demand (kWh)</Form.Label>
            <Form.Control
              type="text"
              value={
                energyFlowAnnual?.daylightDemand !== undefined
                  ? formatNumber2dp(energyFlowAnnual?.daylightDemand || 0)
                  : "N/A"
              }
              disabled
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`daylightEnergyShare-${esys.demandIdx}`}
          >
            <Form.Label>Daylight fulfilled by on-site (%)</Form.Label>
            <Form.Control
              type="text"
              value={getDaylightPercentage(energyFlowAnnual)}
              disabled
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`consumedOnsiteJan-${esys.demandIdx}`}
          >
            <Form.Label>Jan daylight fulfilled by on-site (%)</Form.Label>
            <Form.Control
              type="text"
              value={
                energyFlowMonthly?.[1] !== undefined
                  ? getDaylightPercentage(energyFlowMonthly[1])
                  : "N/A"
              }
              disabled
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`consumedOnsiteJuly-${esys.demandIdx}`}
          >
            <Form.Label>July daylight fulfilled by on-site (%)</Form.Label>
            <Form.Control
              type="text"
              value={
                energyFlowMonthly?.[7] !== undefined
                  ? getDaylightPercentage(energyFlowMonthly[7])
                  : "N/A"
              }
              disabled
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`consumedOnsite-${esys.demandIdx}`}
          >
            <Form.Label>
              Total fulfilled by on-site (%){" "}
              <Tooltip
                position="right"
                text="Behind Meter PV + Behind Meter Battery / Demand"
              />
            </Form.Label>
            <Form.Control
              type="text"
              value={
                (energyFlowAnnual?.demand || 0) > 0
                  ? formatNumber2dp(
                      ((energyFlowAnnual?.behindMeter || 0) /
                        (energyFlowAnnual?.demand || 0)) *
                        100
                    )
                  : 0
              }
              disabled
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`consumedFromIR-${esys.demandIdx}`}
          >
            <Form.Label>
              Total fulfilled by IR (%){" "}
              <Tooltip
                position="right"
                text="Behind Meter PV + Behind Meter Battery + Network Import / Demand"
              />
            </Form.Label>
            <Form.Control
              type="text"
              value={
                (energyFlowAnnual?.demand || 0) > 0
                  ? formatNumber2dp(
                      (((energyFlowAnnual?.behindMeter || 0) +
                        (energyFlowAnnual?.networkImport || 0)) /
                        (energyFlowAnnual?.demand || 0)) *
                        100
                    )
                  : 0
              }
              disabled
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`tenantEnergyShare-${esys.demandIdx}`}
          >
            <Form.Label>
              Tenant energy share (%){" "}
              <Tooltip
                position="right"
                text="Behind Meter PV + Behind Meter Battery / Generation"
              />
            </Form.Label>
            <Form.Control
              type="text"
              value={
                energyFlowAnnual?.tenantEnergyShare
                  ? formatNumber2dp(energyFlowAnnual.tenantEnergyShare * 100)
                  : ""
              }
              disabled
            />
          </Form.Group>
        </Col>
        {site.battery[esys.batteryIdx] &&
          site.battery[esys.batteryIdx].batterySpec && (
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="behindMeterBattery">
                <Form.Label>
                  On-site (Battery) (%){" "}
                  <Tooltip
                    position="right"
                    text="Behind Meter Battery / Behind Meter PV + Behind Meter Battery"
                  />
                </Form.Label>
                <Form.Control
                  type="text"
                  value={
                    energyFlowAnnual?.behindMeter
                      ? formatNumber2dp(
                          (energyFlowAnnual?.behindMeterBattery /
                            energyFlowAnnual?.behindMeter) *
                            100
                        )
                      : ""
                  }
                  disabled
                />
              </Form.Group>
            </Col>
          )}
        {editMode !== "SUMMARY" && (
          <>
            <Col sm={4}>
              <Form.Label>Demand forecast</Form.Label>
              <Form
                style={{
                  display: "flex",
                  fontSize: 12,
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                {Object.entries(DEMAND_FORECAST_LABELS).map(([key, label]) => (
                  <BorderedCheckbox
                    key={`demandForecast-${key}-${esys.demandIdx}`}
                    dataTestId={`demandForecast-${key}-${esys.demandIdx}`}
                    formName={`demand_forecast-${esys.demandIdx}`}
                    value={key}
                    label={label}
                    selectedOption={
                      site.demand[esys.demandIdx]?.demandForecast ?? "flat"
                    }
                    setSelectedOption={(value) =>
                      setSite({
                        demand: {
                          [esys.demandIdx]: {
                            demandForecast: value,
                          },
                        },
                      })
                    }
                  />
                ))}
              </Form>
            </Col>
            {site.demand[esys.demandIdx]?.demandForecast === "changing" && (
              <>
                <Col sm={4}>
                  <Form.Group
                    className="mb-3"
                    controlId={`demandChangePercent-${esys.demandIdx}`}
                  >
                    <Form.Label>Change (%)</Form.Label>
                    <FormInputPercentage
                      value={
                        site.demand[esys.demandIdx]?.demandChangePercent ?? "0"
                      }
                      onChange={(e) =>
                        setSite({
                          demand: {
                            [esys.demandIdx]: {
                              demandChangePercent: e.target.value,
                            },
                          },
                        })
                      }
                      errorKey={`demand.${esys.demandIdx}.demandChangePercent`}
                      testId={`demandChangePercent-${esys.demandIdx}`}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group
                    className="mb-3"
                    controlId={`demandChangeEveryNYears-${esys.demandIdx}`}
                  >
                    <Form.Label>Every N years</Form.Label>
                    <Form.Control
                      type="text"
                      value={
                        site.demand[esys.demandIdx]?.demandChangeEveryNYears ??
                        1
                      }
                      onChange={(e) =>
                        setSite({
                          demand: {
                            [esys.demandIdx]: {
                              demandChangeEveryNYears: e.target.value,
                            },
                          },
                        })
                      }
                      isInvalid={
                        !!errors[
                          `demand.${esys.demandIdx}.demandChangeEveryNYears`
                        ]
                      }
                      data-testid={`demandChangeEveryNYears-${esys.demandIdx}`}
                    />
                    <Form.Control.Feedback type="invalid">
                      {
                        errors[
                          `demand.${esys.demandIdx}.demandChangeEveryNYears`
                        ]
                      }
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </>
            )}
          </>
        )}
      </Row>
      {editMode !== "SUMMARY" && (
        <>
          <Row>
            <Col sm={6}>
              <Form.Group>
                <Stack direction="horizontal">
                  <Form.Label>
                    <strong>Demand shape</strong>
                  </Form.Label>
                  <div className="ms-auto">{demandSource}</div>
                </Stack>
                {demandQualityWarning}
                {!(
                  site.demand[esys.demandIdx].buildingProfile === "noDemand" ||
                  haveDemandCurve
                ) && (
                  <>
                    <Form.Group
                      className="mb-2"
                      controlId={`demandConsumptionTime-${esys.demandIdx}`}
                    >
                      <Form.Select
                        value={site.demand[esys.demandIdx].profileShape}
                        onChange={(e) =>
                          setSite({
                            demand: {
                              [esys.demandIdx]: {
                                profileShape: e.target.value,
                              },
                            },
                          })
                        }
                      >
                        {Object.entries(USAGE_PROFILE_LABELS).map(
                          ([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          )
                        )}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group
                      controlId={`demandConsumptionDays-${esys.demandIdx}`}
                    >
                      <Form.Select
                        value={site.demand[esys.demandIdx].profileDays}
                        onChange={(e) =>
                          setSite({
                            demand: {
                              [esys.demandIdx]: {
                                profileDays: e.target.value,
                              },
                            },
                          })
                        }
                        data-testid={"demandConsumptionDays"}
                      >
                        {Object.entries(USAGE_DAYS_LABELS).map(
                          ([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          )
                        )}
                      </Form.Select>
                    </Form.Group>
                  </>
                )}
                {haveDemandCurve && !displayDemandMetadata && (
                  <div
                    style={{
                      textAlign: "center",
                      color: "rgb(13, 110, 253)",
                    }}
                  >
                    <a onClick={() => setDisplayDemandMetadata(true)}>
                      Show demand metadata
                    </a>
                  </div>
                )}
              </Form.Group>
            </Col>
            {!displayMonthlyBreakdown && (
              <Col>
                <div
                  style={{
                    textAlign: "left",
                    color: "rgb(13, 110, 253)",
                    paddingTop: "30px",
                    paddingLeft: "20%",
                  }}
                >
                  <a
                    onClick={() => setDisplayMonthlyBreakdown(true)}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    Show monthly breakdown
                  </a>
                </div>
              </Col>
            )}
          </Row>
          {haveDemandCurve && displayDemandMetadata && (
            <Row>
              <Col>{renderDemandMetadata()}</Col>
            </Row>
          )}
        </>
      )}
      {!displayMonthlyBreakdown && editMode === "SUMMARY" && (
        <Row>
          <Col>
            <div
              style={{
                textAlign: "left",
                color: "rgb(13, 110, 253)",
              }}
            >
              <a
                onClick={() => setDisplayMonthlyBreakdown(true)}
                style={{
                  cursor: "pointer",
                }}
              >
                Show monthly breakdown
              </a>
            </div>
          </Col>
        </Row>
      )}
      {displayMonthlyBreakdown && (
        <Row className="mt-3">
          <Col>
            <Table
              striped
              bordered
              hover
              size="sm"
              style={{ fontSize: "13px" }}
            >
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Avg demand/day,month (kWh)</th>
                  <th>Avg gen/day,month (kWh)</th>
                  <th>Avg PV disch./day (kWh)</th>
                  <th>Avg Grid disch./day (kWh)</th>
                  <th>
                    Tenant share <br /> (%)
                  </th>
                  <th>
                    Daylight fulfilled <br />
                    by on-site <br /> (%,kWh)
                  </th>
                  <th>
                    Total fulfilled <br /> by on-site (%,kWh)
                  </th>
                  <th>
                    Curtailed <br /> (%,kWh)
                  </th>
                  <th>
                    Exported <br /> (%,kWh)
                  </th>
                  <th style={{ position: "relative" }}>
                    Imported <br /> (kWh)
                    <span
                      style={{
                        position: "absolute",
                        top: "2px",
                        right: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setDisplayMonthlyBreakdown(false)}
                    >
                      ✕
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 12 }, (_, i) => i).map(
                  (index: number) => {
                    const monthData = energyFlowMonthly?.[index + 1];
                    return (
                      <tr key={index}>
                        <td>{months[index]}</td>
                        <td>
                          {monthData !== undefined ? (
                            <>
                              {formatNumber0dp(
                                monthData.demand /
                                  daysInMonth(monthData.year, monthData.month)
                              )}
                              <br />
                              {formatNumber0dp(monthData.demand)}
                            </>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {monthData !== undefined ? (
                            <>
                              {formatNumber0dp(
                                monthData.generation /
                                  daysInMonth(monthData.year, monthData.month)
                              )}
                              <br />
                              {formatNumber0dp(monthData.generation)}
                            </>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {monthData !== undefined
                            ? formatNumber0dp(
                                monthData.behindMeterBattery /
                                  daysInMonth(monthData.year, monthData.month)
                              )
                            : "N/A"}
                        </td>
                        <td>
                          {monthData !== undefined
                            ? formatNumber0dp(
                                monthData.behindMeterBatteryGrid /
                                  daysInMonth(monthData.year, monthData.month)
                              )
                            : "N/A"}
                        </td>
                        <td>
                          {monthData !== undefined &&
                          monthData.generation > 0 ? (
                            <>
                              {formatNumber1dp(
                                (monthData.behindMeter * 100) /
                                  monthData.generation
                              )}
                            </>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {monthData !== undefined ? (
                            <>
                              {getDaylightPercentage(monthData, 1)}
                              <br />
                              {getDaylightKwh(monthData, 0)}
                            </>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {monthData !== undefined && monthData.demand > 0 ? (
                            <>
                              {formatNumber1dp(
                                (monthData.behindMeter * 100) / monthData.demand
                              )}
                              <br />
                              {formatNumber0dp(monthData.behindMeter)}
                            </>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {monthData !== undefined &&
                          monthData.generation > 0 ? (
                            <>
                              {formatNumber1dp(
                                (monthData.curtailed * 100) /
                                  monthData.generation
                              )}
                              <br />
                              {formatNumber0dp(monthData.curtailed)}
                            </>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {monthData !== undefined &&
                          monthData.generation > 0 ? (
                            <>
                              {formatNumber1dp(
                                ((monthData.exported +
                                  monthData.networkExport) *
                                  100) /
                                  monthData.generation
                              )}
                              <br />
                              {formatNumber0dp(
                                monthData.exported + monthData.networkExport
                              )}
                            </>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {monthData !== undefined
                            ? formatNumber0dp(monthData.gridImport)
                            : "N/A"}
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </Table>
            {(energyFlowAnnual?.behindMeter ?? 0) > 0 && (
              <div>
                Assuming perfect matching of demand to generation within each
                month:{" "}
                <ul>
                  <li>
                    Consumed on-site: {formatNumber0dp(maxBehindMeter)} kWh (+
                    {formatNumber0dp(
                      maxBehindMeter - (energyFlowAnnual?.behindMeter ?? 0)
                    )}{" "}
                    kWh, +
                    {formatNumber1dp(
                      (100 *
                        (maxBehindMeter -
                          (energyFlowAnnual?.behindMeter ?? 0))) /
                        (energyFlowAnnual?.behindMeter ?? 1)
                    )}
                    %)
                  </li>
                  <li>
                    Tenant energy share:{" "}
                    {formatNumber2dp(
                      (100 * maxBehindMeter) /
                        (energyFlowAnnual?.generation ?? 1)
                    )}
                    %
                  </li>
                </ul>
              </div>
            )}
          </Col>
        </Row>
      )}
      {editMode !== "SUMMARY" && (
        <GridConnection
          site={site}
          esys={esys as unknown as AllowStringForNumbers<SiteElectricalSystem>}
          setSite={setSite}
          editMode={editMode}
        />
      )}
    </CollapsibleCard>
  );
};

export default Demand;
